import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import moment from "jalali-moment";
import { Button, Spin } from "antd";
import { getChillingRequirementReport } from "@services/api";
import { hejriDate } from "@helpers/financial_helper";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import Chart from "@/components/reports/chilling-requirement/Chart";
import Table from "@/components/reports/chilling-requirement/Table";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import AgroMapFarm from "@/features/map/presentation/components/AgroMapFarm";
import line from "@assets/images/chilling_requirement_report_images/Line185.png";
import frame2182 from "@assets/images/chilling_requirement_report_images/Frame2182.png";
import headerImage from "@assets/images/chilling_requirement_report_images/headerImage.png";
import footerImage from "@assets/images/chilling_requirement_report_images/footerImage.png";
import footerTitle from "@assets/images/chilling_requirement_report_images/footerTitle.png";
import agroyaarPlatform from "@assets/images/chilling_requirement_report_images/agroyaarPlatform.png";
import chillingRequirementIcon from "@assets/images/chilling_requirement_report_images/chillingRequirementIcon.png";

function ChillingRequirementReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tableChillingData, setTableChillingData] = useState([]);
  const [chartChillingData, setChartChillingData] = useState({});

  let pageNumber = 0;

  const chillingReportQuery = useQuery({
    queryKey: ["chillingReportQuery", id],
    queryFn: () => getChillingRequirementReport(id),
  });

  useEffect(() => {
    if (chillingReportQuery?.data) {
      setChartChillingData(chillingReportQuery?.data?.chillingRequirement);
      setTableChillingData(
        chillingReportQuery?.data?.chillingRequirementAnalysisResult
      );
    }
  }, [chillingReportQuery?.data]);

  return (
    <>
      {chillingReportQuery?.isLoading ? (
        <div className="flex flex-row items-center w-[500px] h-[500px] mx-auto my-[6rem]">
          <Spin
            className="spiner-loading-farm-management"
            style={{ margin: "auto" }}
            size="large"
          />
        </div>
      ) : (
        <div className="section-container flex flex-col items-center justify-center w-[720px] mx-auto !bg-white">
          {/* Page1 (Introduction) */}
          <div className="page1">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={headerImage}
                alt="header-image"
                width="750px"
              />

              <div className="flex flex-col justify-between items-center w-full gap-14 mt-[-90px]">
                <p
                  className="text-[#222] text-[36px] font-bold !font-[B-Nazanin]"
                  style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)" }}
                >
                  کشاورزی{" "}
                  <span className="text-president !font-[B-Nazanin]">
                    مطمئن‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر، </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    دقیق‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر و </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    اقتصادی‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر</span>
                  </span>
                </p>

                <div className="flex flex-col items-center">
                  <img
                    src={chillingRequirementIcon}
                    className="w-[287px] h-[296px] !font-[B-Nazanin]"
                    alt="chilling-icon"
                  />
                  <span
                    className="text-[#5700B4] font-bold text-[64px] !font-[B-Nazanin]"
                    style={{
                      textShadow: "1px 0px 3px rgba(118, 118, 118, 0.61)",
                    }}
                  >
                    گزارش تعیین نیاز سرمایی
                  </span>
                </div>

                <p className="text-[#000] font-semibold text-[24px] whitespace-nowrap !font-[B-Nazanin]">
                  هدف ما تصمیم‌گیری برای کشاورز نیست، کمک به تصمیم‌گیری بهتر
                  کشاورز است
                </p>

                <p className="text-president font-bold text-[28px] tracking-[4px]">
                  WWW.AGROYAAR.COM
                </p>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer-image"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={footerTitle}
                  alt="footer-title-image"
                />
                <span className="absolute bottom-1 left-[60px] z-[3] text-[12px] font-bold text-[#fff] tracking-[9px] text-center">
                  WWW.AGROYAAR.COM
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          {/* <!-- Page2 --> */}
          <div className="page2">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={frame2182}
                alt="header-image"
                width="750px"
              />

              <div className="flex flex-row-reverse justify-center items-center w-full gap-5 mt-[-30px] px-4">
                <div className="flex flex-col justify-center items-end w-[50%] gap-2">
                  <div className="flex flex-row justify-end items-center gap-[19px] w-full"></div>
                </div>

                <div className="flex flex-col justify-center items-start w-[50%]">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                    اطلاعات کشاورز
                  </span>

                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <span className="!font-[B-Nazanin]">
                        {chartChillingData?.farmer?.firstName}{" "}
                        {chartChillingData?.farmer?.lastName}
                      </span>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">کد کشاورزی:</span>
                        <span className="!font-sans text-[16px]">
                          {chartChillingData?.farmer?.farmerCode}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">استان و شهر:</span>
                        <span className="!font-[B-Nazanin]">
                          {chartChillingData?.farmer?.province?.name}،{" "}
                          {chartChillingData?.farmer?.city?.name}
                        </span>
                      </div>
                    </div>

                    <img
                      src={
                        process.env.REACT_APP_ARVAN_S3_BASE_URL +
                        chartChillingData?.farmer?.avatar
                      }
                      alt="profile-image"
                      width="70px"
                      height="70px"
                      className="rounded-full border-[2px] border-solid border-president"
                    />
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="750px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-row justify-center items-end w-full gap-7 px-4">
                <div className="flex flex-col justify-center items-start w-[50%]">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                    اطلاعات زمین
                  </span>

                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">نام قطعه:</span>
                        <span className="!font-[B-Nazanin]">
                          {chartChillingData?.season?.farm?.name}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]"> استان:</span>
                        <span className="!font-[B-Nazanin]">
                          {chartChillingData?.season?.farm?.province?.name}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">شهر:</span>
                        <span className="!font-[B-Nazanin]">
                          {chartChillingData?.season?.farm?.city?.name}
                        </span>
                      </div>
                    </div>

                    <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                      <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                      <AgroMapFarm
                        mapId={chartChillingData?.season?.farm?.id}
                        type="onlyShow"
                        toolbarOptions={toolbarBaseControls}
                        markerIcon={FieldMarkerIcon}
                        currentFarmPolygon={
                          chartChillingData?.season?.farm?.polygon
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin] items-start">
                    اطلاعات فصل رشد
                  </span>
                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">نام محصول:</span>
                        <span className="!font-[B-Nazanin]">
                          {chartChillingData?.season?.product?.persianName}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">مساحت زمین</span>
                        <span className="!font-[B-Nazanin]">
                          {chartChillingData?.season?.area} هکتار
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">فصل رشد:</span>
                        <span className="!font-[B-Nazanin]">
                          {moment(
                            `${chartChillingData?.season?.growingSeason}-04-01`,
                            "YYYY-MM-DD"
                          )
                            .locale("fa")
                            .format("YYYY")}{" "}
                          {chartChillingData?.season?.persianName
                            ? ` - ${chartChillingData?.season?.persianName}`
                            : ""}
                        </span>
                      </div>
                    </div>

                    <img
                      src={chartChillingData?.season?.product?.imageUrl}
                      alt="product-name-image"
                      width="80px"
                      height="80px"
                      className="rounded-full"
                    />
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="750px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <h2 className="text-agrotext text-lg max-lg:text-sm font-semibold py-2 !font-[B-Nazanin]">
                نمودار زیر، روند تجمیع واحد سرمایی را بر اساس اطلاعات ایستگاه
                هواشناسی{" "}
                <span className="text-[#460f81e7] !font-[B-Nazanin]">
                  ({chartChillingData?.weatherStation?.name})
                </span>{" "}
                نشان می‌دهد
              </h2>

              <div className="flex flex-col items-center justify-center w-full mt-[-10px]">
                <Chart chartChillingData={chartChillingData} />

                <div className="flex flex-col items-start mt-[-10px]">
                  <div className="flex flex-row gap-2 text-[#333] font-semibold text-lg">
                    <h3 className="!font-[B-Nazanin]">
                      بازه تعیین نیاز سرمایی:
                    </h3>
                    <p className="font-semibold !font-[B-Nazanin]">
                      {hejriDate(chartChillingData?.dormancyDate)} -{" "}
                      {hejriDate(chartChillingData?.selectedDate)}
                    </p>
                  </div>

                  <div className="NiazSarmaii flex flex-row justify-center items-center gap-3 rounded-2xl">
                    <span className="text-right text-[#333333] text-lg max-lg:text-xs font-semibold !font-[B-Nazanin]">
                      مقدار انباشت سرمایی
                    </span>
                    <div className="px-4 max-lg:px-4 !rounded-2xl justify-center items-center flex">
                      <span
                        className="text-center text-purple-900 text-lg max-lg:text-sm font-semibold !font-[B-Nazanin]"
                        dir="ltr"
                      >
                        {chartChillingData?.totalUtahNumber}
                      </span>
                    </div>
                    <span className=" text-right text-[#333333] text-lg max-lg:text-xs font-semibold !font-[B-Nazanin]">
                      واحد سرمایی بوده است
                    </span>
                  </div>
                </div>
              </div>

              <span className="w-full text-right text-[#404848] !font-[B-Nazanin] text-lg max-lg:text-xs font-bold mb-[-10px] px-4">
                در جدول زیر وضعیت تامین نیاز سرمایی ارقام{" "}
                {chartChillingData?.farm?.product?.persianName} ارائه شده است
              </span>

              <div className="mb-24">
                <Table tableChillingData={tableChillingData} />
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer-image"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer-image"
                />
                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {chartChillingData?.chillingRequirementCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[20px] right-[100px] max-lg:right-[32px] px-8 text-[18px]  w-[150px]"
              id="download-button"
              onClick={() => window.print()}
            >
              <p>پرینت گزارش</p>
            </Button>
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[65px] right-[100px] max-lg:right-[32px] px-8 text-[18px] w-[150px]"
              id="download-button"
              onClick={() => window.close()}
            >
              <p> بازگشت</p>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default ChillingRequirementReport;
